import moment, { Moment } from 'moment';
import { CalendarEventOutput } from '../calendar-types';

export interface EventSegment {
  event: CalendarEventOutput;
  effectiveStart: Moment;
  effectiveEnd: Moment;
  /** True if this segment is a continuation from a previous day */
  isContinuation: boolean;
  /** True if this segment continues into the next day */
  continuesNextDay: boolean;
}

export interface EventLayout {
  segment: EventSegment;
  col: number;
  totalCols: number;
}

/**
 * Split events that cross midnight into one segment per calendar day.
 * An 11pm–1am event becomes: [Sun 11pm–midnight, Mon midnight–1am].
 */
export function expandToSegments(events: CalendarEventOutput[]): EventSegment[] {
  const segments: EventSegment[] = [];
  for (const event of events) {
    if (!event.startDateTime || !event.endDateTime) continue;
    const start = moment(event.startDateTime);
    const end = moment(event.endDateTime);

    let dayStart = start.clone().startOf('day');
    while (dayStart.isBefore(end)) {
      const dayEnd = dayStart.clone().add(1, 'day');
      const segStart = moment.max(start.clone(), dayStart.clone());
      const segEnd = moment.min(end.clone(), dayEnd.clone());
      if (segStart.isBefore(segEnd)) {
        segments.push({
          event,
          effectiveStart: segStart,
          effectiveEnd: segEnd,
          isContinuation: segStart.valueOf() > start.valueOf(),
          continuesNextDay: segEnd.valueOf() < end.valueOf(),
        });
      }
      dayStart.add(1, 'day');
    }
  }
  return segments;
}

/**
 * Google Calendar-style overlap layout.
 * Greedily assigns columns within an overlap group, then widens
 * totalCols so every event in the group gets the same column count.
 */
export function computeOverlapLayout(segments: EventSegment[]): EventLayout[] {
  if (segments.length === 0) return [];

  const sorted = [...segments].sort((a, b) => {
    const diff = a.effectiveStart.valueOf() - b.effectiveStart.valueOf();
    if (diff !== 0) return diff;
    // Longer segments go left (earlier column)
    return b.effectiveEnd.diff(b.effectiveStart) - a.effectiveEnd.diff(a.effectiveStart);
  });

  const colEnds: number[] = [];
  const cols: number[] = new Array(sorted.length);

  for (let i = 0; i < sorted.length; i++) {
    const startMs = sorted[i].effectiveStart.valueOf();
    const endMs = sorted[i].effectiveEnd.valueOf();
    let placed = false;
    for (let c = 0; c < colEnds.length; c++) {
      if (startMs >= colEnds[c]) {
        colEnds[c] = endMs;
        cols[i] = c;
        placed = true;
        break;
      }
    }
    if (!placed) {
      cols[i] = colEnds.length;
      colEnds.push(endMs);
    }
  }

  const result: EventLayout[] = sorted.map((segment, i) => ({
    segment,
    col: cols[i],
    totalCols: 1,
  }));

  for (let i = 0; i < result.length; i++) {
    const aStart = result[i].segment.effectiveStart.valueOf();
    const aEnd = result[i].segment.effectiveEnd.valueOf();
    const group = result.filter(({ segment: s }) =>
      s.effectiveStart.valueOf() < aEnd && s.effectiveEnd.valueOf() > aStart
    );
    const totalCols = Math.max(...group.map((r) => r.col)) + 1;
    group.forEach((r) => { if (r.totalCols < totalCols) r.totalCols = totalCols; });
  }

  return result;
}
